import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from './stores/auth.store';

// Lazy load route components
const LoginPage = lazy(() => import('./pages/login/LoginPage'));
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const OcrTestPage = lazy(() => import('./pages/ocr-test/OcrTestPage'));
const ApiKeysPage = lazy(() => import('./pages/api-keys/ApiKeysPage'));
const UsagePage = lazy(() => import('./pages/UsagePage'));
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'));
const PrivacyPage = lazy(() => import('./pages/privacy/PrivacyPage'));
const AdminUsersPage = lazy(() => import('./pages/admin/UsersPage'));
// Note: DocsPage will be added in a future task
// const DocsPage = lazy(() => import('./pages/docs/DocsPage'));

/**
 * Loading fallback component
 * Displays a centered spinner while route components are loading
 */
const LoadingFallback = () => (
  <div className="flex h-screen items-center justify-center" role="status" aria-live="polite">
    <div className="text-center">
      <div
        className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
        aria-hidden="true"
      />
      <p className="mt-4 text-gray-600">Loading...</p>
      <span className="sr-only">Loading page content</span>
    </div>
  </div>
);

/**
 * Protected Route wrapper component
 * Handles authentication check and role-based access control
 *
 * @param requireAdmin - If true, requires user to have admin role
 */
interface ProtectedRouteProps {
  requireAdmin?: boolean;
}

const ProtectedRoute = ({ requireAdmin = false }: ProtectedRouteProps) => {
  const { isAuthenticated, user, isLoading } = useAuthStore();

  // Show loading state while checking authentication
  if (isLoading) {
    return <LoadingFallback />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    // Save the current location to redirect back after login
    const currentPath = window.location.pathname + window.location.search;
    const redirectPath =
      currentPath !== '/login' ? `?redirect=${encodeURIComponent(currentPath)}` : '';
    return <Navigate to={`/login${redirectPath}`} replace />;
  }

  // TODO: Handle force password change redirect
  // This will be implemented when password change functionality is added
  // if (user.forcePasswordChange) {
  //   return <Navigate to="/change-password" replace />;
  // }

  // Check admin role if required
  if (requireAdmin && user.role !== 'admin') {
    // Redirect to dashboard with error message
    return (
      <Navigate
        to="/dashboard"
        replace
        state={{ error: 'Access denied: Admin privileges required' }}
      />
    );
  }

  // Render child routes with suspense boundary
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Outlet />
    </Suspense>
  );
};

/**
 * Admin Route wrapper component
 * Shorthand for ProtectedRoute with admin requirement
 */
const AdminRoute = () => <ProtectedRoute requireAdmin />;

/**
 * Public Route wrapper component
 * Redirects to dashboard if user is already authenticated
 */
const PublicRoute = () => {
  const { isAuthenticated, isLoading } = useAuthStore();

  // Show loading state while checking authentication
  if (isLoading) {
    return <LoadingFallback />;
  }

  // Redirect to dashboard if already authenticated
  if (isAuthenticated) {
    // Check for redirect parameter from protected route
    const params = new URLSearchParams(window.location.search);
    const redirect = params.get('redirect');

    if (redirect && redirect.startsWith('/')) {
      return <Navigate to={redirect} replace />;
    }

    return <Navigate to="/dashboard" replace />;
  }

  // Render public routes with suspense boundary
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Outlet />
    </Suspense>
  );
};

/**
 * Main routes configuration
 *
 * Route Structure:
 * - / - Redirect to /dashboard
 * - /login - Login page (public)
 * - /dashboard - Dashboard home (protected)
 * - /ocr-test - OCR upload test (protected)
 * - /api-keys - API key management (protected)
 * - /usage - Usage statistics (protected)
 * - /settings - User settings (protected)
 * - /privacy - Privacy/PDPA settings (protected)
 * - /admin/users - User management (admin only)
 * - /docs - API documentation (protected) [TODO: Implement DocsPage]
 */
export const AppRoutes = () => {
  return (
    <Routes>
      {/* Root redirect to dashboard */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      {/* Public routes */}
      <Route element={<PublicRoute />}>
        <Route path="/login" element={<LoginPage />} />
      </Route>

      {/* Protected routes */}
      <Route element={<ProtectedRoute />}>
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/ocr-test" element={<OcrTestPage />} />
        <Route path="/api-keys" element={<ApiKeysPage />} />
        <Route path="/usage" element={<UsagePage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        {/* TODO: Add /docs route when DocsPage is implemented */}
        {/* <Route path="/docs" element={<DocsPage />} /> */}
      </Route>

      {/* Admin-only routes */}
      <Route element={<AdminRoute />}>
        <Route path="/admin/users" element={<AdminUsersPage />} />
      </Route>

      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};
